import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';

const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { lang } = useDirection();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setError(tr('login.fillAll', lang));
      return;
    }
    setIsLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || tr('login.invalidCreds', lang));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left decorative panel */}
      <Box
        sx={{
          width: '46%',
          background: 'linear-gradient(155deg, #0F172A 0%, #1E293B 55%, #312E81 100%)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          px: 8,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative glows */}
        <Box
          sx={{
            position: 'absolute',
            top: -120,
            right: -120,
            width: 440,
            height: 440,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 340,
            height: 340,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 7 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '1rem',
                color: '#FFF',
                letterSpacing: '-0.5px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              OS
            </Box>
            <Typography
              sx={{ color: '#F8FAFC', fontWeight: 700, fontSize: '1.3rem', fontFamily: 'Inter, sans-serif' }}
            >
              OpSelect
            </Typography>
          </Box>

          <Typography
            variant="h3"
            sx={{
              color: '#F8FAFC',
              fontWeight: 800,
              mb: 2.5,
              lineHeight: 1.15,
              fontSize: '2.4rem',
            }}
          >
            {tr('login.headline1', lang)}
            <br />
            {tr('login.headline2', lang)}
          </Typography>
          <Typography
            sx={{
              color: '#94A3B8',
              fontSize: '1rem',
              lineHeight: 1.75,
              maxWidth: 340,
            }}
          >
            {tr('login.appSubtitle', lang)}
          </Typography>
        </motion.div>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F8FAFC',
          px: 4,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ width: '100%', maxWidth: 400 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#0F172A' }}>
            {tr('login.title', lang)}
          </Typography>
          <Typography sx={{ color: '#64748B', mb: 4, fontSize: '0.9rem' }}>
            {tr('login.desc', lang)}
          </Typography>

          {error && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            </motion.div>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              name="email"
              label={tr('login.email', lang)}
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
              autoFocus
              sx={{ mb: 2 }}
            />
            <TextField
              name="password"
              label={tr('login.password', lang)}
              type="password"
              fullWidth
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !formData.email || !formData.password}
            >
              {isLoading ? <CircularProgress size={22} color="inherit" /> : tr('login.signIn', lang)}
            </Button>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default LoginForm;
